import { useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { TypeToConfirmDialog } from "@/components/form";

import { ForeignNationalsAPI } from "../../../../api/foreignNationals";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  firstName: string;
  lastName: string;
};

/** Type-the-last-name confirmation; navigates to /clients once deleted. */
export const DeleteClientDialog: FC<Props> = ({
  open,
  onOpenChange,
  clientId,
  firstName,
  lastName
}) => {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const expected = lastName.trim();

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await ForeignNationalsAPI.delete(clientId);
      toast.success("Client deleted", {
        description: "This client has been removed from your lists."
      });
      onOpenChange(false);
      navigate("/clients");
    } catch (e) {
      toast.error("Couldn't delete client", {
        description:
          e instanceof Error ? e.message : "Please try again in a moment."
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <TypeToConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Delete client?"
      description={
        <>
          This will delete{" "}
          <span className="font-medium text-foreground">
            {[firstName.trim(), expected].filter(Boolean).join(" ")}
          </span>
          , including their cases, documents and requests. This action cannot be
          undone.
        </>
      }
      expected={expected}
      expectedNoun="last name"
      missingExpectedMessage="This client is missing a last name, so deletion can't be confirmed."
      isPending={isDeleting}
      onConfirm={() => {
        handleDelete().catch(() => undefined);
      }}
    />
  );
};
